(() => {
  const measurementId = 'G-TQ9P3818G9';
  const allowedEvents = new Set([
    'collection_product_click',
    'collection_add_to_cart',
    'collection_related_guide_click',
    'collection_related_collection_click',
  ]);
  const queue = [];
  const pendingAdds = new Map();
  let analyticsAllowed = false;
  let googleQueueReady = false;

  const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const internalDestinationPath = (value) => {
    if (!value) return '';
    try {
      const destination = new URL(value, window.location.href);
      return destination.origin === window.location.origin ? destination.pathname : '';
    } catch (_) {
      return '';
    }
  };

  const collectionContext = () => {
    const heading = document.querySelector('main h1');
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    return {
      collection_handle: pathParts[0] === 'collections' ? pathParts[1] || '' : '',
      collection_title: cleanText(heading?.textContent).replace(/^Collection:\s*/i, ''),
      collection_path: window.location.pathname,
    };
  };

  const setStatus = (status, eventName = '') => {
    document.documentElement.dataset.tcgkingCollectionGa4Status = status;
    if (eventName) document.documentElement.dataset.tcgkingCollectionGa4LastEvent = eventName;
  };

  const cardData = (card) => {
    if (!card) return {};
    const productLink = card.querySelector('a[href*="/products/"]');
    const price = Number(card.dataset.productPriceCents || 0) / 100;
    return {
      product_id: card.dataset.productId || '',
      variant_id: card.dataset.variantId || '',
      product_handle: card.dataset.productHandle || productLink?.pathname?.split('/products/')[1]?.split(/[?#]/)[0] || '',
      product_title: cleanText(card.dataset.productTitle || productLink?.getAttribute('aria-label') || productLink?.textContent),
      value: Number.isFinite(price) ? price : 0,
      currency: card.dataset.currency || '',
    };
  };

  const normalisePayload = (eventData) => {
    const payload = { ...collectionContext(), ...eventData };
    const destinationPath = internalDestinationPath(payload.link_url || payload.product_url);
    if (destinationPath) payload.destination_path = destinationPath;
    const itemId = payload.variant_id || payload.product_id;
    if (itemId && !payload.items) {
      payload.items = [{
        item_id: String(itemId),
        item_name: payload.product_title || payload.product_handle || String(itemId),
        price: Number(payload.value || 0),
        quantity: Number(payload.quantity || 1),
      }];
    }
    payload.event_source = 'shopify_collection';
    payload.send_to = measurementId;
    return payload;
  };

  const publishShopifyDiagnostic = (eventName, payload) => {
    try {
      const result = window.Shopify?.analytics?.publish?.(`tcgking:${eventName}`, payload);
      if (result?.catch) result.catch(() => {});
    } catch (_) {
      // GA4 dispatch must not depend on Shopify's optional diagnostic channel.
    }
  };

  const flush = () => {
    if (!analyticsAllowed || !googleQueueReady || typeof window.gtag !== 'function') return;
    while (queue.length) {
      const { eventName, eventData } = queue.shift();
      const payload = normalisePayload(eventData);
      window.gtag('event', eventName, payload);
      publishShopifyDiagnostic(eventName, payload);
      setStatus('dispatched', eventName);
      document.dispatchEvent(new CustomEvent('tcgking:collection-event-dispatched', {
        detail: { eventName, payload },
      }));
    }
  };

  const initialiseGoogleQueue = () => {
    if (!analyticsAllowed || googleQueueReady) return;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag('config', measurementId, { send_page_view: false });
    googleQueueReady = true;
    setStatus('ready');
  };

  const updateConsent = () => {
    const privacy = window.Shopify?.customerPrivacy;
    analyticsAllowed = Boolean(privacy?.analyticsProcessingAllowed?.());
    if (analyticsAllowed) {
      initialiseGoogleQueue();
      flush();
    } else {
      setStatus('waiting_for_consent');
    }
  };

  window.tcgkingTrackCollectionEvent = (eventName, eventData = {}) => {
    if (!allowedEvents.has(eventName)) return;
    queue.push({ eventName, eventData });
    setStatus(analyticsAllowed ? 'queued' : 'waiting_for_consent', eventName);
    if (analyticsAllowed) {
      initialiseGoogleQueue();
      flush();
    }
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;

    const guideSection = link.closest('[data-tcgking-collection-related-guides], .uc-guides');
    if (guideSection) {
      window.tcgkingTrackCollectionEvent('collection_related_guide_click', {
        link_url: link.href,
        link_text: cleanText(link.textContent || link.querySelector('img')?.alt),
      });
      return;
    }

    const directory = link.closest('[data-tcgking-collection-directory], .compact-directory');
    if (directory) {
      const group = link.closest('.compact-directory__link-group');
      window.tcgkingTrackCollectionEvent('collection_related_collection_click', {
        link_url: link.href,
        link_text: cleanText(link.textContent),
        collection_group: cleanText(group?.querySelector('strong')?.textContent).toLowerCase(),
        destination_collection_handle: link.pathname.split('/collections/')[1]?.split(/[?#]/)[0] || '',
      });
      return;
    }

    const productGrid = link.closest('#product-grid, [id="product-grid"], .product-grid');
    const card = link.closest('[data-tcgking-product-card], .product-card-wrapper');
    if (!productGrid || !card || !link.pathname.includes('/products/')) return;

    window.tcgkingTrackCollectionEvent('collection_product_click', {
      ...cardData(card),
      link_url: link.href,
      click_target: link.classList.contains('tcg-product-card__media-link') || Boolean(link.querySelector('img'))
        ? 'product_image'
        : 'product_title',
    });
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('product-form form');
    const card = form?.closest('[data-tcgking-product-card], .product-card-wrapper');
    if (!form || !card || !form.closest('#product-grid, [id="product-grid"], .product-grid')) return;
    const variantId = String(new FormData(form).get('id') || card.dataset.variantId || '');
    if (variantId) pendingAdds.set(variantId, cardData(card));
  }, true);

  const subscribeToCart = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      window.setTimeout(subscribeToCart, 50);
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'product-form') return;
      const variantId = String(event.productVariantId || '');
      const pending = pendingAdds.get(variantId);
      if (!pending) return;
      pendingAdds.delete(variantId);
      window.tcgkingTrackCollectionEvent('collection_add_to_cart', {
        ...pending,
        variant_id: variantId,
        quantity: Number(event.cartData?.quantity || 1),
      });
    });
    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      const variantId = String(event?.productVariantId || '');
      if (variantId) pendingAdds.delete(variantId);
    });
  };

  subscribeToCart();
  document.addEventListener('visitorConsentCollected', updateConsent);
  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) {
        setStatus('consent_api_error');
        return;
      }
      updateConsent();
    });
  } else {
    setStatus('consent_api_unavailable');
  }
})();
